// UpgradePrompt — contextual nudge when a Starter user hits a Pro-gated feature.
//
// Behavior branches on account type:
//   • Regular user   → generic "Upgrade to Pro" CTA, routes to /app/upgrade
//   • Demo account   → "Claim First Closer #N to unlock" CTA, routes to
//                      /register?ref=<stored code> after logging out of demo.
//                      Sale happens on the exact feature that hooked the tester.
//
// Usage:
//   <UpgradePrompt
//     feature="AI Rehab Scan"
//     headline="Drop photos. Claude scopes the rehab."
//     reason="Pro users get 5 AI Rehab Scans per day."
//     from="ai_scan"
//   />

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Crown, ArrowRight, Rocket, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

export default function UpgradePrompt({
  feature = "Pro feature",
  headline,
  reason,
  from,                // optional ?from= context that the upgrade page reads
  variant = "card",    // "card" | "inline"
  testid = "upgrade-prompt",
}) {
  const { user, logout } = useAuth?.() || { user: null, logout: () => {} };
  const isDemo = !!user?.is_demo_account;

  // ─────────────────────────────────────────────────────────────────────
  // Demo mode → First Closer conversion moment
  // ─────────────────────────────────────────────────────────────────────
  if (isDemo) {
    return (
      <FirstCloserUpsell
        feature={feature}
        headline={headline}
        reason={reason}
        from={from}
        variant={variant}
        testid={testid}
        logout={logout}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // Regular Starter user → standard Pro upsell
  // ─────────────────────────────────────────────────────────────────────
  const href = `/app/upgrade${from ? `?from=${encodeURIComponent(from)}` : ""}`;

  if (variant === "inline") {
    return (
      <Link
        to={href}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--primary-dark)] bg-[var(--primary)]/12 hover:bg-[var(--primary)]/20 px-2.5 py-1 rounded-full transition-colors"
        data-testid={testid}
      >
        <Crown size={11} /> Upgrade to unlock {feature}
        <ArrowRight size={11} />
      </Link>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-amber-50 via-white to-[var(--primary)]/5 border border-amber-200 rounded-2xl p-6 text-center"
      data-testid={testid}
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center mx-auto mb-4">
        <Crown size={22} />
      </div>
      <h3 className="font-display font-bold text-lg text-[var(--ink)] mb-2">
        {headline || `${feature} is a Pro feature`}
      </h3>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed mb-4 max-w-md mx-auto">
        {reason || `Upgrade to Pro to unlock ${feature} — and the rest of the AI co-pilot.`}
      </p>
      <Link
        to={href}
        className="btn-money inline-flex"
        data-testid={`${testid}-cta`}
      >
        <Crown size={13} /> See what Pro unlocks <ArrowRight size={13} />
      </Link>
      <div className="text-[11px] text-[var(--ink-3)] mt-3">
        14-day free trial · No credit card required · Cancel any time
      </div>
    </div>
  );
}


/**
 * The exact-feature-moment sale. Demo user is playing with a feature that's
 * gated — instead of showing them a generic "Upgrade to Pro" wall, we show
 * a First Closer CTA with the live cohort number. Sale is 3 taps away.
 */
function FirstCloserUpsell({ feature, headline, reason, from, variant, testid, logout }) {
  const nav = useNavigate();
  const [seatSnap, setSeatSnap] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/early-adopter/stats`)
      .then(({ data }) => { if (!cancelled) setSeatSnap(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const nextCohort = seatSnap ? Math.min(seatSnap.total_first_closers + 1, seatSnap.cap) : null;
  const seatsLeft = seatSnap?.seats_left ?? null;
  const capReached = !!seatSnap?.cap_reached;

  const handleClaim = async () => {
    const ref = (localStorage.getItem("af_ref_code") || "").toUpperCase();
    track("demo_locked_feature_claim_clicked", {
      feature, from, cohort: nextCohort, has_ref: !!ref,
    });
    await logout();
    const params = new URLSearchParams();
    if (ref) params.set("ref", ref);
    if (from) params.set("from", from);
    const qs = params.toString();
    nav(`/register${qs ? `?${qs}` : ""}`, { replace: true });
  };

  // ── Inline variant — small pill for chip-like placements ──────────────
  if (variant === "inline") {
    return (
      <button
        type="button"
        onClick={handleClaim}
        className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 px-3 py-1.5 rounded-full shadow-sm hover:shadow transition-all"
        data-testid={testid}
      >
        <Rocket size={11} />
        {capReached
          ? `Join the waitlist to unlock ${feature}`
          : (nextCohort
              ? `Claim First Closer #${nextCohort} to unlock ${feature}`
              : `Claim your First Closer seat to unlock ${feature}`)}
      </button>
    );
  }

  // ── Card variant — full contextual moment ──────────────────────────────
  const primaryCta = capReached
    ? "Join the waitlist"
    : (nextCohort ? `Claim First Closer #${nextCohort}` : "Claim your First Closer seat");

  return (
    <div
      className="relative overflow-hidden bg-gradient-to-br from-amber-50 via-white to-amber-100 border-2 border-amber-300 rounded-2xl p-6 text-center shadow-md"
      data-testid={testid}
    >
      {/* Corner ribbon */}
      {seatsLeft != null && !capReached && (
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-amber-900 bg-amber-200 border border-amber-400 rounded-full px-2 py-0.5">
          <Sparkles size={9} /> Only {seatsLeft} seats left
        </div>
      )}

      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center mx-auto mb-4 shadow-lg">
        <Rocket size={26} />
      </div>

      <div className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-800 mb-1.5">
        You&rsquo;re in the demo · Ready to close for real?
      </div>

      <h3 className="font-display font-black text-xl md:text-2xl tracking-tight text-slate-900 mb-2 leading-tight">
        {headline || `Unlock ${feature} the moment you claim your seat`}
      </h3>

      <p className="text-sm text-slate-700 leading-relaxed mb-5 max-w-md mx-auto">
        {reason || `${feature} is one of the plays your First Closer badge unlocks — plus 100 marketplace credits and 40% off for life. Every seat past #100 pays full price forever.`}
      </p>

      <button
        type="button"
        onClick={handleClaim}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-[0.08em] text-white bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 shadow-lg hover:shadow-xl transition-all"
        data-testid={`${testid}-cta`}
      >
        <Rocket size={14} /> {primaryCta}
        <ArrowRight size={14} />
      </button>

      <div className="text-[11px] text-slate-500 mt-3 leading-relaxed">
        40% off · Life · 100 marketplace credits · Priority Jarvis routing
      </div>
    </div>
  );
}
